// Google map for dispatch (every driver) and for a driver's phone (just their route):
// lab, numbered stops, the planned road route, the trail driven, and where each phone is right now.
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { S, drivers, driverColor, driverStops, unassigned, stopPoint, officeName, driverName, isToday } from '../store.js';
import { ago, fmtTime, esc, gmapsTo } from '../util.js';

const STALE_MS = 3 * 60000;
let map = null; let lib = null; let mountedEl = null; let fittedFor = null; let info = null;
let only = null; // driver email when this is a driver's phone map
let markers = new Map(); let lines = [];
let loadFailed = false;

async function load() {
  if (lib) return lib;
  setOptions({ key: import.meta.env.VITE_MAPS_BROWSER_KEY, v: 'weekly' });
  // LatLngBounds lives in 'core', not 'maps'.
  const [core, maps, geometry] = await Promise.all([importLibrary('core'), importLibrary('maps'), importLibrary('geometry')]);
  lib = { LatLngBounds: core.LatLngBounds, LatLng: core.LatLng, Map: maps.Map, Polyline: maps.Polyline, InfoWindow: maps.InfoWindow, Pin: pinClass(maps.OverlayView, core.LatLng), geometry };
  return lib;
}

// Our own HTML pins on an overlay. Google's Advanced Markers silently show nothing when the Map ID
// isn't accepted, so the app draws its pins itself; these work on any map.
function pinClass(OverlayView, LatLng) {
  // Plain method names only: Google's OverlayView (an MVCObject) already owns 'map', 'position',
  // 'title' and addListener, and overriding those breaks it.
  return class Pin extends OverlayView {
    constructor(m, pos, content, title) {
      super();
      this.wdrEl = document.createElement('div');
      this.wdrEl.className = 'pin-wrap';
      this.wdrEl.addEventListener('click', (e) => { e.stopPropagation(); this.wdrTap?.(); });
      this.wdrPos = pos;
      this.update(pos, content, title);
      this.setMap(m);
    }
    update(pos, content, title) {
      this.wdrPos = pos;
      if (this.wdrEl.firstChild !== content) this.wdrEl.replaceChildren(content);
      this.wdrEl.title = title || ''; this.wdrEl.setAttribute('aria-label', title || '');
      this.draw();
    }
    latLng() { return new LatLng(this.wdrPos.lat, this.wdrPos.lng); }
    onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.wdrEl); }
    draw() {
      const proj = this.getProjection && this.getProjection(); if (!proj) return;
      const pt = proj.fromLatLngToDivPixel(this.latLng()); if (!pt) return;
      this.wdrEl.style.left = `${pt.x}px`; this.wdrEl.style.top = `${pt.y}px`;
    }
    onRemove() { this.wdrEl.remove(); }
  };
}

/** opts.driver: show only that driver's route (driver phone view). */
export async function mountMap(el, opts = {}) {
  if (mountedEl === el && map) { if (only !== (opts.driver || null)) { only = opts.driver || null; fittedFor = null; updateMap(); } return; }
  mountedEl = el; map = null; markers = new Map(); lines = []; fittedFor = null; info = null; only = opts.driver || null;
  try {
    const L = await load();
    if (mountedEl !== el) return;
    const opts = {
      center: { lat: S.lab.lat, lng: S.lab.lng }, zoom: 10,
      mapId: import.meta.env.VITE_MAPS_MAP_ID || 'DEMO_MAP_ID',
      streetViewControl: false, mapTypeControl: false, fullscreenControl: true, clickableIcons: false,
      gestureHandling: only ? 'greedy' : 'auto', // one finger pans on a phone
    };
    if (google.maps.ColorScheme) opts.colorScheme = google.maps.ColorScheme.FOLLOW_SYSTEM;
    map = new L.Map(el, opts);
  } catch (e) {
    console.error('Google Maps failed to load', e);
    loadFailed = true;
    el.innerHTML = '<div class="empty">The map didn\'t load. Check the Google Maps browser key and Map ID in web/.env, and that the key allows this site\'s address.</div>';
    return;
  }
  updateMap();
}

function pin(text, color, { faded = false, square = false, hollow = false } = {}) {
  const d = document.createElement('div');
  d.className = `pin${square ? ' sq' : ''}${hollow ? ' hollow' : ''}`;
  d.style.setProperty('--c', color);
  if (faded) d.style.opacity = '0.45';
  d.textContent = text;
  return d;
}

function setMarker(key, position, content, title, popup) {
  let m = markers.get(key);
  try {
    if (!m) {
      m = new lib.Pin(map, position, content, title);
      // Tap a stop for its name, ETA and a Directions button.
      m.wdrTap = () => { if (!m.wdrPopup || !lib.InfoWindow) return; info = info || new lib.InfoWindow(); info.setContent(m.wdrPopup); info.setPosition(m.latLng()); info.open({ map }); };
      markers.set(key, m);
    } else {
      m.update(position, content, title);
    }
    m.wdrPopup = popup || null;
    m.wdrSeen = true;
  } catch (e) { console.error('Pin failed', key, e); } // one bad pin never blanks the map
}

function stopPopup(s, n, p) {
  const eta = s.status === 'delivered' ? `Done ${fmtTime(s.deliveredAt)}` : s.eta ? `ETA ${fmtTime(s.eta)}` : '';
  return `<div class="mpop"><b>${n}. ${esc(officeName(s))}</b>${eta ? `<div>${esc(eta)}</div>` : ''}${s.status === 'pending' ? `<a href="${esc(gmapsTo(p))}" target="_blank" rel="noopener">Directions</a>` : ''}</div>`;
}

function line(path, color, { dashed = false, weight = 4, opacity = 0.85 } = {}) {
  const o = { map, path, strokeColor: color, strokeWeight: weight, strokeOpacity: dashed ? 0 : opacity, clickable: false };
  if (dashed) o.icons = [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: opacity, scale: 3 }, offset: '0', repeat: '14px' }];
  lines.push(new lib.Polyline(o));
}

export function updateMap() {
  if (!map || !lib) return;
  // A drawing problem should never blank the map or break the rest of the screen.
  try { draw(); } catch (e) { console.error('Map drawing failed', e); }
}

function draw() {
  markers.forEach((m) => { m.wdrSeen = false; });
  lines.forEach((l) => l.setMap(null)); lines = [];
  const bounds = new lib.LatLngBounds();
  const lab = { lat: S.lab.lat, lng: S.lab.lng };
  bounds.extend(lab);
  setMarker('lab', lab, pin('Lab', '#0a0a0a', { square: true }), S.lab.name);

  for (const d of drivers().filter((x) => !only || x.email === only)) {
    const color = driverColor(d.email);
    const list = driverStops(d.email);
    list.forEach((s, i) => {
      const p = stopPoint(s); if (!p) return;
      bounds.extend(p);
      setMarker(`s_${s.id}`, p, pin(String(i + 1), color, { faded: s.status !== 'pending' }),
        `${i + 1}. ${officeName(s)} (${driverName(d.email)})${s.status === 'delivered' ? `, delivered ${fmtTime(s.deliveredAt)}` : s.eta ? `, ETA ${fmtTime(s.eta)}` : ''}`,
        stopPopup(s, i + 1, p));
    });
    const r = S.routes[d.email] || {};
    const planned = r.remainingPolyline || r.plannedPolyline;
    if (planned) line(lib.geometry.encoding.decodePath(planned), color, { dashed: true, weight: 3 });
    // Farthest first: the drive out from the lab to the first stop is planned separately.
    if (!r.remainingPolyline && r.leadPolyline) line(lib.geometry.encoding.decodePath(r.leadPolyline), color, { dashed: true, weight: 3 });
    else {
      const pts = list.filter((s) => s.status === 'pending').map(stopPoint).filter(Boolean);
      if (pts.length) line([r.pos && isToday() ? r.pos : lab, ...pts], color, { dashed: true, weight: 2, opacity: 0.5 });
    }
    const trail = (S.tracks[d.email] || []).map((p) => ({ lat: p.lat, lng: p.lng }));
    if (trail.length > 1) line(trail, color, { weight: 5, opacity: 0.9 });
    if (r.pos && isToday()) {
      const stale = Date.now() - (r.pos.ts || 0) > STALE_MS;
      const initials = (d.name || d.email).split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
      const el = pin(initials, stale ? '#9ca3af' : color, { hollow: true });
      if (!stale) el.classList.add('live');
      setMarker(`p_${d.email}`, r.pos, el, `${d.name}: ${stale ? 'last seen' : 'here'} ${ago(r.pos.ts)}`);
      bounds.extend(r.pos);
    }
  }
  for (const s of only ? [] : unassigned()) {
    const p = stopPoint(s); if (!p) continue;
    bounds.extend(p);
    setMarker(`s_${s.id}`, p, pin('?', '#9ca3af', { hollow: true }), `${officeName(s)} (needs a driver)`);
  }
  markers.forEach((m, k) => { if (!m.wdrSeen) { m.setMap(null); markers.delete(k); } });
  // Fit once per day viewed, so dispatch can pan and zoom without the map jumping back.
  const fitKey = `${S.date}|${only || ''}`;
  if (fittedFor !== fitKey && markers.size > 1) { map.fitBounds(bounds, only ? 36 : 48); fittedFor = fitKey; }
}

/** Zoom back out to the whole route (the driver map's Show whole route button). */
export function refitMap() { fittedFor = null; updateMap(); }

export const mapFailed = () => loadFailed;
